import { IUpdateCharge } from "Dashboard/scenes/Resources/domain/IUpdateCharge";
import popoverClasses from "../../../../../../components/Popover/popover.module.scss";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import NumberInput from "Dashboard/components/NumberInput";
import { averageConsumptionSuffix, consumptionLabel, haveAverageConsumption } from "Dashboard/scenes/Resources/helpers/resourceUtil";
import { useSelector } from "react-redux";
import { selectState, setResourceUpdateChargePopoverState, updateCharge, updateChargeTemporarily } from "../charges.slice";
import { selectResourceDetails } from "../../Details/details.slice";
import classes from "../../../styles/ResourceUpdateChargePopover.module.scss";
import { FormatNumber } from "utils/numberUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import { useAppDispatch } from "hooks/hooks";
import { toast } from "react-toastify";

const ResourceUpdateChargePopover = () => {

    const dispatch = useAppDispatch();
    const { updating } = useSelector(selectState);
    const resourceDetails = useSelector(selectResourceDetails);
    const [incorrectUsageAtStart, setIncorrectUsageAtStart] = useState<boolean>(false);
    const [incorrectUsageAtEnd, setIncorrectUsageAtEnd] = useState<boolean>(false);
    const [currentUsageAtStart, setCurrentUsageAtStart] = useState<number>(0);
    const [currentUsageAtEnd, setCurrentUsageAtEnd] = useState<number | undefined>(undefined);
    const [currentAverageConsumption, setCurrentAverageConsumption] = useState<number>(0);

    const {
        handleSubmit,
        register,
        control,
        formState: { errors },
    } = useForm<IUpdateCharge>();

    const close = () => {
        dispatch(setResourceUpdateChargePopoverState({ isOpen: false }));
    };

    const onChangeUsageAtStart = (newUsageAmountAtStart: number) => {
        setCurrentUsageAtStart(newUsageAmountAtStart);
    }

    const onChangeUsageAtEnd = (newUsageAmountAtEnd: number) => {
        setCurrentUsageAtEnd(newUsageAmountAtEnd);
    }

    const onChangeAverageConsumption = (newAverageConsumption: number) => {
        setCurrentAverageConsumption(newAverageConsumption);
    }

    useEffect(() => {
        if (updating) {
            setCurrentUsageAtStart(updating.usageAmountAtStart);
            setCurrentUsageAtEnd(updating.usageAmountAtEnd);
            setCurrentAverageConsumption(updating.averageConsumption);
        }
    }, [updating]);

    useEffect(() => {
        if (updating) {
            const usageAmountAtStartValid = updating.usageAmountAtEnd === undefined || updating.usageAmountAtEnd === null
                || currentUsageAtStart <= updating.usageAmountAtEnd;
            const usageAmountAtEndValid = currentUsageAtEnd === undefined || currentUsageAtEnd === null
                || updating.usageAmountAtStart <= currentUsageAtEnd;
            setIncorrectUsageAtStart(!usageAmountAtStartValid);
            setIncorrectUsageAtEnd(!usageAmountAtEndValid);
        }
    }, [updating, currentUsageAtStart, currentUsageAtEnd]);

    const onSubmit = async (data: IUpdateCharge) => {
        if (resourceDetails) {
            if (incorrectUsageAtStart) {
                toast.warn(`${consumptionLabel(resourceDetails.resource.usageUnit)} na početku nije u dozvoljenom opsegu`);
                return;
            }
            if (incorrectUsageAtEnd) {
                toast.warn(`${consumptionLabel(resourceDetails.resource.usageUnit)} na kraju nije u dozvoljenom opsegu`);
                return;
            }
            dispatch(updateChargeTemporarily(data));
            await dispatch(updateCharge(data)).unwrap();
            toast.success("Izmena je uspešno zabeležena.");
        } else {
            toast.warn("Neočekivana greška");
        }
        close();
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}
            className={`${popoverClasses.basicPopoverView} ${popoverClasses.gPopoverInputs}`}
        >
            <input
                type="hidden"
                value={updating?.resource.resourceId}
                {...register("resourceId")}
            />

            <input
                type="hidden"
                value={updating?.chargeId}
                {...register("chargeId")}
            />

            {resourceDetails && haveAverageConsumption(resourceDetails.resource.category) && (
                <div
                    className={`${errors.usageAmountAtStart || incorrectUsageAtStart === true ? popoverClasses.inputError : ""}`}
                >
                    <div>
                        <label>
                            {resourceDetails.resource &&
                                consumptionLabel(resourceDetails.resource.usageUnit)}
                            {" "} na početku
                        </label>
                        <Controller
                            name="usageAmountAtStart"
                            control={control}
                            defaultValue={undefined}
                            rules={{
                                required: true,
                            }}
                            render={({ field: { onChange } }) => (
                                <NumberInput
                                    formatAs="quantity"
                                    onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                                        onChange({ target: { value: parsedValue } });
                                        onChangeUsageAtStart(parsedValue);
                                    }}
                                    value={`${currentUsageAtStart}`}
                                    className={classes.inputField}
                                />
                            )}
                        />
                    </div>
                </div>
            )}

            {resourceDetails && haveAverageConsumption(resourceDetails.resource.category) && updating?.usageAmountAtEnd && (
                <div
                    className={`${errors.usageAmountAtEnd || incorrectUsageAtEnd === true ? popoverClasses.inputError : ""}`}
                >
                    <div>
                        <label>
                            {resourceDetails.resource &&
                                consumptionLabel(resourceDetails.resource.usageUnit)}
                            {" "} na kraju
                        </label>
                        <Controller
                            name="usageAmountAtEnd"
                            control={control}
                            defaultValue={undefined}
                            rules={{
                                required: true,
                            }}
                            render={({ field: { onChange } }) => (
                                <NumberInput
                                    formatAs="quantity"
                                    onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                                        onChange({ target: { value: parsedValue } });
                                        onChangeUsageAtEnd(parsedValue);
                                    }}
                                    value={`${currentUsageAtEnd}`}
                                    className={classes.inputField}
                                />
                            )}
                        />
                    </div>
                </div>
            )}

            {resourceDetails && haveAverageConsumption(resourceDetails.resource.category) && (
                <div
                    className={`${errors.averageConsumption ? popoverClasses.inputError : ""}`}
                >
                    <div>
                        <label>
                            prosečna potrošnja ({resourceDetails?.resource.usageUnit &&
                                averageConsumptionSuffix(
                                    resourceDetails.resource.usageUnit
                                )})
                        </label>
                        <Controller
                            name="averageConsumption"
                            control={control}
                            defaultValue={undefined}
                            rules={{
                                required: true,
                            }}
                            render={({ field: { onChange } }) => (
                                <NumberInput
                                    formatAs="quantity"
                                    onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                                        onChange({ target: { value: parsedValue } });
                                        onChangeAverageConsumption(parsedValue);
                                    }}
                                    value={`${currentAverageConsumption}`}
                                    className={classes.inputField}
                                />
                            )}
                        />
                    </div>
                </div>
            )}

            <div className={popoverClasses.buttons}>
                <button type="submit" className={popoverClasses.iconButton}>
                    <FontAwesomeIcon
                        icon={faLongArrowAltDown}
                        className={popoverClasses.icon} />
                </button>
            </div>
        </form>
    );
}

export default ResourceUpdateChargePopover;
